import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Filters,
  Icon,
  Page,
  PageHeader,
  Table,
  rowLinkClasses,
  type Action,
  type FilterFieldSlot,
} from '@evogard/ui';
import type { Beneficiary } from '@evogard/contract';
import {
  BENEFICIARY_REF_TYPES,
  loadFailureMessage,
  type BeneficiaryRefType,
} from '@evogard/domain';
import { api, ok } from '../../lib/api.js';
import { useDebouncedTerm } from '../../lib/debounce.js';
import { truncationNotice } from '../../lib/list.js';
import { can, useSessionContext } from '../../lib/session.js';
import {
  BENEFICIARIES_KEY,
  BeneficiaryFormModal,
} from '../../components/beneficiary/BeneficiaryFormModal.js';
import { BeneficiaryDetailModal } from '../../components/beneficiary/BeneficiaryDetailModal.js';
import {
  ACCOUNT_TYPE_LABELS,
  REF_TYPE_LABELS,
  bankLabel,
  ownerKindLabel,
} from '../../components/beneficiary/labels.js';

const PER_PAGE = 50;

/**
 * Beneficiários — quem recebe pagamento da Evogard, e como pagar.
 *
 * A lista mostra o NOME do dono, lido da tabela dele na hora (empresa,
 * terceiro) ou do espelho do ERP (associado). Quando não dá para ler, a linha
 * diz isso, em vez de mostrar um vazio que pareça "sem nome".
 *
 * O cadastro é o `BeneficiaryFormModal`, o mesmo que qualquer módulo abre.
 * Sem `requires` na rota: quem protege é a API (`platform.beneficiaries:ver`),
 * e o menu esconde o item de quem não tem.
 */
export function BeneficiariesScreen() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const client = useQueryClient();
  const canCreate = can(context, 'platform.beneficiaries', 'criar');
  const canSeeBank = can(context, 'platform.beneficiaries', 'ver_dados_bancarios');
  /* editar precisa dos números inteiros — com a máscara, salvar a gravaria */
  const canEdit = canSeeBank && can(context, 'platform.beneficiaries', 'editar');

  const [term, setTerm] = useState('');
  const search = useDebouncedTerm(term.trim());
  /*
   * O recorte do painel `Filters`: vale quando alguém confirma, e não a cada
   * clique. A busca é da barra e vale ao digitar — o cabeçalho de `Filters`
   * explica a divisão.
   */
  const [refType, setRefType] = useState<BeneficiaryRefType | ''>('');
  const [showArchived, setShowArchived] = useState(false);

  const fields: FilterFieldSlot[] = [
    {
      type: 'selecao',
      name: 'tipo',
      label: 'Tipo de conta',
      placeholder: 'Todos',
      options: BENEFICIARY_REF_TYPES.map((t) => ({
        value: t,
        label: REF_TYPE_LABELS[t],
      })),
    },
    {
      type: 'selecao',
      name: 'situacao',
      label: 'Situação',
      placeholder: 'Só os ativos',
      help: 'Arquivado não aparece para escolher numa despesa, mas continua no histórico.',
      options: [{ value: 'todos', label: 'Ativos e arquivados' }],
    },
  ];
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState<{ id: number; editing: boolean } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: [...BENEFICIARIES_KEY, tenantId, search, refType, showArchived],
    queryFn: async () => {
      const r = await api.beneficiaries.listBeneficiaries({
        query: {
          page: 1,
          perPage: PER_PAGE,
          search: search || undefined,
          beneficiaryRefType: refType || undefined,
          includeArchived: showArchived ? 'true' : undefined,
        },
      });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'as contas de pagamento'));
      return r.body;
    },
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === tenantId ? previous : undefined,
  });

  const toggle = useMutation({
    mutationFn: async ({ b, archived }: { b: Beneficiary; archived: boolean }) => {
      const r = await api.beneficiaries.updateBeneficiary({
        params: { id: b.id },
        body: { archived },
      });
      return ok(r, 200, 'Não foi possível arquivar a conta de pagamento.');
    },
    onMutate: () => setActionError(null),
    onSuccess: () => void client.invalidateQueries({ queryKey: BENEFICIARIES_KEY }),
    onError: (e: Error) => setActionError(e.message),
  });

  const items = query.data?.items ?? [];

  return (
    <Page measure="cheia">
      <PageHeader
        title="Contas de pagamento"
        actions={
          canCreate && (
            <Button icon="mais" onClick={() => setCreating(true)}>
              Nova conta de pagamento
            </Button>
          )
        }
      >
        Quem recebe pagamento da Evogard — prestador, instituição, terceiro ou associado —
        e a conta ou a chave PIX para pagar. O mesmo dono pode ter mais de uma conta.
      </PageHeader>

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {actionError && <Alert tone="erro">{actionError}</Alert>}

      {(!query.isError || query.data) && (
        <Card
          title={
            query.data === undefined
              ? 'Carregando…'
              : `${query.data.total} conta(s) de pagamento`
          }
          bleed
        >
          <Table
            paginate={false}
            search={{
              label: 'Pesquisar contas de pagamento',
              placeholder:
                'Nome, CPF/CNPJ de prestador ou instituição, código do associado',
              value: term,
              onChange: setTerm,
            }}
            toolbar={
              <Filters
                values={{ tipo: refType, situacao: showArchived ? 'todos' : '' }}
                fields={fields}
                onApply={(values) => {
                  setRefType(String(values.tipo ?? '') as BeneficiaryRefType | '');
                  setShowArchived(values.situacao === 'todos');
                }}
              />
            }
            label="Contas de pagamento da base, com o dono e o jeito de pagar"
            items={items}
            loading={query.isLoading}
            rowKey={(b) => String(b.id)}
            empty={
              search || refType || showArchived
                ? 'Nenhuma conta de pagamento com esses filtros.'
                : 'Nenhuma conta de pagamento cadastrada nesta base.'
            }
            rowTitle={(b) => b.owner.name ?? 'Nome indisponível'}
            rowSubtitle={(b) => ownerKindLabel(b)}
            rowIcon="dinheiro"
            /*
             * A seta abre os dados bancários sem máscara — só para quem tem
             * `ver_dados_bancarios`. Sem ela, não há seta: a linha continua
             * mostrando agência e conta mascaradas, e o PIX inteiro.
             */
            rowLink={
              canSeeBank
                ? (b) => (
                    <button
                      type="button"
                      aria-label={`Ver os dados bancários de ${b.owner.name ?? 'esta conta'}`}
                      className={rowLinkClasses}
                      onClick={() => setDetail({ id: b.id, editing: false })}
                    >
                      <Icon nome="seta-direita" size={16} />
                    </button>
                  )
                : undefined
            }
            rowActions={(b): Action[] => {
              if (!canEdit) return [];
              const archived = Boolean(b.archivedAt);
              return [
                {
                  label: 'Editar dados bancários',
                  icon: 'etiqueta',
                  disabled: archived
                    ? 'A conta de pagamento está arquivada. Reative antes de editar.'
                    : (false as const),
                  run: () => setDetail({ id: b.id, editing: true }),
                },
                {
                  label: archived ? 'Reativar' : 'Arquivar',
                  icon: archived ? 'atualizar' : 'bloqueado',
                  run: () => toggle.mutate({ b, archived: !archived }),
                },
              ];
            }}
            columns={[
              {
                title: 'Quem recebe',
                alignment: 'inicio' as const,
                minWidth: '14rem',
                cell: (b) => <Owner b={b} />,
              },
              {
                title: 'Conta bancária',
                alignment: 'inicio' as const,
                minWidth: '14rem',
                cell: (b) =>
                  b.bankCode || b.accountNumber ? (
                    <span className="text-sm">
                      {bankLabel(b.bankCode, b.bankName)}
                      <span className="mt-0.5 block text-xs text-texto-suave">
                        Ag. {b.agency ?? '—'} · {b.accountNumber ?? '—'}
                        {b.accountType ? ` · ${ACCOUNT_TYPE_LABELS[b.accountType]}` : ''}
                      </span>
                    </span>
                  ) : (
                    <span className="text-texto-suave">Sem conta</span>
                  ),
              },
              {
                title: 'PIX',
                alignment: 'inicio' as const,
                cell: (b) =>
                  b.pixKey ? (
                    <span className="break-all text-sm">{b.pixKey}</span>
                  ) : (
                    <span className="text-texto-suave">Sem chave</span>
                  ),
              },
            ]}
          />
          {truncationNotice(query.data?.total, items.length) && (
            <p className="border-t border-borda-suave px-4 py-3 text-xs text-texto-suave">
              {truncationNotice(query.data?.total, items.length)}
            </p>
          )}
        </Card>
      )}

      <BeneficiaryFormModal open={creating} onOpenChange={setCreating} />
      <BeneficiaryDetailModal
        key={detail ? `${detail.id}-${detail.editing}` : 'fechado'}
        beneficiaryId={detail?.id ?? null}
        startEditing={detail?.editing}
        onClose={() => setDetail(null)}
      />
    </Page>
  );
}

function Owner({ b }: { b: Beneficiary }) {
  return (
    <span className={b.archivedAt ? 'text-texto-suave' : undefined}>
      {b.owner.readable ? (
        b.owner.name
      ) : (
        <span className="text-atencao">
          {b.beneficiaryRefType === 'associado'
            ? `Associado ${b.beneficiaryRefId} — nome fora do espelho do ERP`
            : 'Cadastro do dono não encontrado'}
        </span>
      )}
      {b.archivedAt && (
        <Badge tone="neutro" className="ml-2">
          Arquivado
        </Badge>
      )}
      <span className="mt-0.5 block text-xs text-texto-suave">
        {ownerKindLabel(b)}
        {/* o documento do PAGAMENTO; o do dono só no beneficiário de antes de 24/09 */}
        {(b.document ?? b.owner.document) ? ` · ${b.document ?? b.owner.document}` : ''}
        {b.accountHolderName ? ` · titular ${b.accountHolderName}` : ''}
      </span>
    </span>
  );
}
